// src/shared/components/ui/ConfirmModal/ConfirmModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { ModalCenter } from '../ModalCenter';
import type { ModalCenterRef } from '../ModalCenter/types';
import { EnterIcon, EscIcon } from '../icons';

export interface ConfirmModalRef {
  /**
   * Abre la modal de confirmación.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   * @param onCancel Callback que se ejecutará si el usuario cancela (opcional).
   * @param title Título de la modal (opcional).
   * @param message Mensaje de la modal (opcional).
   * @param confirmText Texto del botón de confirmar (opcional).
   * @param cancelText Texto del botón de cancelar (opcional).
   */
  open: (
    onConfirm: () => void | Promise<void>,
    onCancel?: () => void,
    title?: string,
    message?: string,
    confirmText?: string,
    cancelText?: string,
    renderContent?: () => React.ReactNode
  ) => void;
}

export const ConfirmModal = forwardRef<ConfirmModalRef>((_, ref) => {
  const { t } = useTranslation('ui');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void | Promise<void>>(() => { });
  const cancelRef = useRef<(() => void) | undefined>(undefined);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    renderContent?: () => React.ReactNode;
  }>({
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
  });

  useImperativeHandle(ref, () => ({
    open: (
      onConfirm: () => void | Promise<void>,
      onCancel?: () => void,
      title?: string,
      message?: string,
      confirmText?: string,
      cancelText?: string,
      renderContent?: () => React.ReactNode
    ) => {
      confirmRef.current = onConfirm;
      cancelRef.current = onCancel;
      setModalConfig({
        title: title || t('confirmModal.defaultTitle'),
        message: message || t('confirmModal.defaultMessage'),
        confirmText: confirmText || t('confirmModal.defaultConfirmText'),
        cancelText: cancelText || t('confirmModal.defaultCancelText'),
        renderContent,
      });
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    await confirmRef.current();
    confirmRef.current = () => { };
    cancelRef.current = undefined;
    modalRef.current?.closeModal();
  };

  const handleCancel = () => {
    // Siempre ejecutar el callback de cancelación si existe
    if (cancelRef.current) {
      cancelRef.current();
    }
    // Limpiar referencias
    confirmRef.current = () => { };
    cancelRef.current = undefined;
    // Siempre cerrar la modal
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={modalConfig.title}
      type="default"
      width="default"
    >
      <div className="space-y-4">
        <p className="text-sm whitespace-pre-line">{modalConfig.message}</p>
        {modalConfig.renderContent?.()}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={handleCancel}>
            {modalConfig.cancelText}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            {modalConfig.confirmText}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ConfirmModal.displayName = 'ConfirmModal';
